/**
 * Step ordering inside the `(setup)` route group, as a pure function.
 *
 * `(setup)/_layout.tsx` compares the requested route against the server's
 * `current_step` and redirects back if it is ahead; `(setup)/index.tsx` uses the
 * same rule to dispatch a resume. Backward navigation stays allowed — that is
 * just Back.
 */

export type SetupRouteName =
  | 'username'
  | 'welcome'
  | 'permissions'
  | 'intent'
  | 'taste'
  | 'friends'
  | 'done';

export type SetupRouteInput = {
  /** Auth-session claim. Sits outside the 1..6 counter; social signups only. */
  usernamePending: boolean;
  /** Server `current_step`, 1..6. `null` means onboarding has not started. */
  currentStep: number | null;
  requestedRoute: SetupRouteName;
};

/**
 * Index + 1 is the server's `current_step`. `username` is deliberately absent:
 * it is the auth-owned account-setup claim, not one of onboarding's 6 steps, so
 * "step 3 of 6" means the same thing for both cohorts.
 */
const STEP_ROUTES: readonly SetupRouteName[] = [
  'welcome',
  'permissions',
  'intent',
  'taste',
  'friends',
  'done',
] as const;

const FIRST_STEP = 1;
const LAST_STEP = STEP_ROUTES.length;

/** Clamps a possibly absent or out-of-range server value into 1..6. */
function normalizeStep(currentStep: number | null): number {
  if (currentStep === null || !Number.isFinite(currentStep)) return FIRST_STEP;
  return Math.min(Math.max(Math.trunc(currentStep), FIRST_STEP), LAST_STEP);
}

function routeForStep(step: number): SetupRouteName {
  return STEP_ROUTES[step - 1];
}

/** `null` for `username`, which is not part of the step counter. */
function stepForRoute(route: SetupRouteName): number | null {
  const index = STEP_ROUTES.indexOf(route);
  return index === -1 ? null : index + 1;
}

/**
 * Returns the route to redirect to, or `null` when the requested route is
 * allowed.
 */
export function resolveSetupRoute(input: SetupRouteInput): SetupRouteName | null {
  const { usernamePending, currentStep, requestedRoute } = input;

  if (usernamePending) {
    return requestedRoute === 'username' ? null : 'username';
  }

  const step = normalizeStep(currentStep);
  const currentRoute = routeForStep(step);

  // The claim is done; they should not sit on it.
  if (requestedRoute === 'username') return currentRoute;

  const requestedStep = stepForRoute(requestedRoute);
  if (requestedStep === null) return currentRoute;

  // Ahead of the server's progress: send them back to where they actually are.
  // At or behind is allowed — that is just Back.
  return requestedStep > step ? currentRoute : null;
}
